import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Toolbar,
  AppBar,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  createChecklist,
  createChecklistGrupo,
  createChecklistPergunta,
  createChecklistResposta,
} from "../../../api/checkilstApi";
import { Empresa } from "../../../types/empresas";
import {
  GrupoPergunta,
  Pergunta,
  Resposta,
} from "../../../types/checklistData";
import { getEmpresas } from "../../../api/empresaApi";
import GroupDialog from "./parts/GroupDialog";
import QuestionDialog from "./parts/QuestionDialog";
import AnswerDialog from "./parts/AnswerDialog";
import ChecklistPreview from "./parts/ChecklistPreview";

const steps = ["Checklist", "Grupos", "Perguntas", "Respostas", "Revisão"];

type DraftChecklist = {
  id?: number;
  descricao_checklist: string;
  template_checklist?: string | null;
  legenda: string | null;
  fotos_checklist: boolean;
  assinatura_checklist: boolean;
  id_empresa_ferramenta: number;
  id_empresa: number | null;
};

export default function ChecklistBuilder() {
  const token = localStorage.getItem("token");
  const [activeStep, setActiveStep] = useState(0);

  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [checklist, setChecklist] = useState<DraftChecklist>({
    descricao_checklist: "",
    fotos_checklist: true,
    assinatura_checklist: false,
    id_empresa_ferramenta: 2,
    id_empresa: null,
    template_checklist: null,
    legenda: null,
  });

  const [grupos, setGrupos] = useState<GrupoPergunta[]>([]);
  const [grupoSelecionadoId, setGrupoSelecionadoId] = useState<number | null>(
    null,
  );

  const [perguntaSelecionadaId, setPerguntaSelecionadaId] = useState<
    number | null
  >(null);

  const currentGrupo = useMemo(
    () => grupos.find((g) => g.id === grupoSelecionadoId) || null,
    [grupos, grupoSelecionadoId],
  );
  const currentPergunta = useMemo(
    () =>
      currentGrupo?.perguntas.find((p) => p.id === perguntaSelecionadaId) ||
      null,
    [currentGrupo, perguntaSelecionadaId],
  );

  const [openGroup, setOpenGroup] = useState(false);
  const [openQuestion, setOpenQuestion] = useState(false);
  const [openAnswer, setOpenAnswer] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!token) return;
        const data = await getEmpresas(token);
        setEmpresas(data);
      } catch {
        toast.error("Erro ao buscar empresas");
      }
    })();
  }, [token]);

  const handleCreateChecklist = async () => {
    try {
      if (!token) throw new Error("Sem token");
      if (!checklist.descricao_checklist) {
        toast.warn("Informe a descrição do checklist");
        return;
      }
      const payload = {
        descricao_checklist: checklist.descricao_checklist,
        fotos_checklist: checklist.fotos_checklist,
        assinatura_checklist: checklist.assinatura_checklist,
        id_empresa_ferramenta: checklist.id_empresa_ferramenta,
        template_checklist: checklist.template_checklist,
        legenda: checklist.legenda,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await createChecklist(token, payload as any);
      setChecklist((prev) => ({ ...prev, id: res.id }));
      toast.success("Checklist criado");
      setActiveStep(1);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      toast.error("Erro ao criar checklist");
    }
  };

  const handleAddGrupo = async (dados: {
    descricao: string;
    ajuda?: string;
    rodape?: string;
    imagemBase64?: string | null;
  }) => {
    try {
      if (!token || !checklist.id)
        throw new Error("Checklist ainda não criado");
      const body = {
        descricao_checklist_grupo: dados.descricao,
        ajuda_checklist_grupo: dados.ajuda || null,
        rodape_checklist_grupo: dados.rodape || null,
        id_checklist: checklist.id,
        data: dados.imagemBase64 || null,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await createChecklistGrupo(token, body as any);
      const novo: GrupoPergunta = {
        id: res.id,
        descricao_checklist_grupo: dados.descricao,
        ajuda_checklist_grupo: body.ajuda_checklist_grupo,
        rodape_checklist_grupo: body.rodape_checklist_grupo,
        imagem_checklist_grupo: null,
        ordem_checklist_grupo: (grupos.length || 0) + 1,
        perguntas: [],
        id_checklist: checklist.id!,
        legenda: null
      };
      setGrupos((prev) => [...prev, novo]);
      setGrupoSelecionadoId(res.id);
      toast.success("Grupo criado");
      setActiveStep(2);
    } catch {
      toast.error("Erro ao criar grupo");
    }
  };

  const handleAddPergunta = async (dados: {
    descricao: string;
    obrigatoria: boolean;
    ordem?: number;
  }) => {
    try {
      if (!token || !grupoSelecionadoId) throw new Error("Selecione um grupo");

      const ordemDesejada =
        typeof dados.ordem === "number" && dados.ordem > 0
          ? dados.ordem
          : (currentGrupo?.perguntas.length || 0) + 1;

      const ordemJaUsada = currentGrupo?.perguntas.some(
        (p) => p.ordem_checklist_pergunta === ordemDesejada,
      );

      if (ordemJaUsada) {
        toast.warn(
          `Já existe uma pergunta com a ordem ${ordemDesejada} neste grupo. Escolha outra ordem.`,
        );
        return;
      }

      const body = {
        descricao_checklist_pergunta: dados.descricao,
        obrigatorio_checklist_pergunta: dados.obrigatoria,
        id_checklist_grupo: grupoSelecionadoId,
        ordem_checklist_pergunta: ordemDesejada,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await createChecklistPergunta(token, body as any);

      const nova: Pergunta = {
        id: res.id,
        descricao_checklist_pergunta: dados.descricao,
        obrigatorio_checklist_pergunta: dados.obrigatoria,
        id_checklist_grupo: grupoSelecionadoId,
        ordem_checklist_pergunta: body.ordem_checklist_pergunta,
        respostas: [],
      };

      setGrupos((prev) =>
        prev.map((g) =>
          g.id === grupoSelecionadoId
            ? { ...g, perguntas: [...g.perguntas, nova] }
            : g,
        ),
      );
      setPerguntaSelecionadaId(res.id);
      toast.success("Pergunta criada");
      setActiveStep(3);
    } catch {
      toast.error("Erro ao criar pergunta");
    }
  };

  const handleAddResposta = async (dados: {
    descricao:
      | "Desc"
      | "Done"
      | "Block"
      | "NA"
      | "Atenção"
      | "I"
      | "Regular"
      | "Normal"
      | "Agendar Troca"
      | "Trocar";
    valor: string;
    ordem: number;
  }) => {
    try {
      if (!token || !currentPergunta) throw new Error("Selecione uma pergunta");
      const body = {
        descricao_checklist_resposta: dados.descricao,
        ordem_checklist_resposta: dados.ordem,
        id_checklist_pergunta: currentPergunta.id,
        valor_checklist_respostas: dados.valor,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await createChecklistResposta(token, body as any);
      const nova: Resposta = {
        id: res.id,
        descricao_checklist_resposta: body.descricao_checklist_resposta!,
        valor_checklist_respostas: body.valor_checklist_respostas,
      };
      setGrupos((prev) =>
        prev.map((g) =>
          g.id === currentPergunta.id_checklist_grupo
            ? {
                ...g,
                perguntas: g.perguntas.map((p) =>
                  p.id === currentPergunta.id
                    ? { ...p, respostas: [...p.respostas, nova] }
                    : p,
                ),
              }
            : g,
        ),
      );
      toast.success("Resposta criada");
    } catch {
      toast.error("Erro ao criar resposta");
    }
  };

  const StepChecklist = (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Dados do Checklist
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Descrição"
              fullWidth
              value={checklist.descricao_checklist}
              onChange={(e) =>
                setChecklist((prev) => ({
                  ...prev,
                  descricao_checklist: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Template"
              fullWidth
              value={checklist.template_checklist}
              onChange={(e) =>
                setChecklist((prev) => ({
                  ...prev,
                  template_checklist: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Legenda"
              fullWidth
              value={checklist.legenda}
              onChange={(e) =>
                setChecklist((prev) => ({
                  ...prev,
                  legenda: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Fotos no Checklist"
              fullWidth
              value={checklist.fotos_checklist ? "true" : "false"}
              onChange={(e) =>
                setChecklist((prev) => ({
                  ...prev,
                  fotos_checklist: e.target.value === "true",
                }))
              }
            >
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Assinatura no Checklist"
              fullWidth
              value={checklist.assinatura_checklist ? "true" : "false"}
              onChange={(e) =>
                setChecklist((prev) => ({
                  ...prev,
                  assinatura_checklist: e.target.value === "true",
                }))
              }
            >
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Empresa"
              fullWidth
              value={checklist.id_empresa ?? ""}
              onChange={(e) =>
                setChecklist((prev) => ({
                  ...prev,
                  id_empresa: e.target.value ? Number(e.target.value) : null,
                }))
              }
            >
              <option value=""></option>
              {empresas.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.nome_empresa}
                </option>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Box mt={3} display="flex" gap={2}>
          <Button
            variant="contained"
            onClick={handleCreateChecklist}
            disabled={!checklist.descricao_checklist}
          >
            Salvar e ir para Grupos
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const StepGrupos = (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Grupos</Typography>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => setOpenGroup(true)}
          >
            Novo Grupo
          </Button>
        </Box>
        <Divider sx={{ mt: 2 }} />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Use a árvore à esquerda para selecionar/editar.
        </Typography>
      </CardContent>
    </Card>
  );

  const StepPerguntas = (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6">
            {currentGrupo
              ? `Perguntas — ${currentGrupo.descricao_checklist_grupo}`
              : "Selecione um grupo"}
          </Typography>
          <Button
            startIcon={<Add />}
            variant="contained"
            disabled={!currentGrupo}
            onClick={() => setOpenQuestion(true)}
          >
            Nova Pergunta
          </Button>
        </Box>
        <Divider />
        {!currentGrupo && (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Selecione um grupo na árvore.
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const StepRespostas = (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6">
            {currentPergunta
              ? `Respostas — ${currentPergunta.descricao_checklist_pergunta}`
              : "Selecione uma pergunta"}
          </Typography>
          <Button
            startIcon={<Add />}
            variant="contained"
            disabled={!currentPergunta}
            onClick={() => setOpenAnswer(true)}
          >
            Nova Resposta
          </Button>
        </Box>
        <Divider />
        {!currentPergunta && (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Selecione uma pergunta na árvore.
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const StepRevisao = (
    <Card>
      <CardContent>
        <Typography variant="h6">Revisão</Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography>
          <b>Checklist:</b> {checklist.descricao_checklist}{" "}
          {checklist.id && `(ID ${checklist.id})`}
        </Typography>
        <Box mt={2}>
          {grupos
            .sort((a, b) => a.ordem_checklist_grupo - b.ordem_checklist_grupo)
            .map((g) => (
              <Box key={g.id} mb={2}>
                <Typography>
                  <b>
                    {g.ordem_checklist_grupo}. {g.descricao_checklist_grupo}
                  </b>
                </Typography>
                {g.perguntas
                  .sort(
                    (a, b) =>
                      a.ordem_checklist_pergunta - b.ordem_checklist_pergunta,
                  )
                  .map((p) => (
                    <Box key={p.id} ml={2} mt={0.5}>
                      <Typography>
                        • {p.ordem_checklist_pergunta}.{" "}
                        {p.descricao_checklist_pergunta}{" "}
                        {p.obrigatorio_checklist_pergunta && "(Obrigatória)"}
                      </Typography>
                      {p.respostas.map((r) => (
                        <Typography key={r.id} ml={3} color="text.secondary">
                          - {r.descricao_checklist_resposta} (
                          {r.valor_checklist_respostas})
                        </Typography>
                      ))}
                    </Box>
                  ))}
              </Box>
            ))}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box m={2}>
      <AppBar
        position="static"
        elevation={0}
        color="transparent"
        sx={{ mb: 2 }}
      >
        <Toolbar disableGutters>
          <Typography variant="h5">Criar Checklist</Typography>
        </Toolbar>
      </AppBar>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8} lg={12}>
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && StepChecklist}
          {activeStep === 1 && StepGrupos}
          {activeStep === 2 && StepPerguntas}
          {activeStep === 3 && StepRespostas}
          {activeStep === 4 && StepRevisao}

          <Box mt={2} display="flex" justifyContent="space-between">
            <Button
              disabled={activeStep === 0}
              onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
            >
              Voltar
            </Button>
            <Button
              disabled={
                (activeStep === 0 && !checklist.id) ||
                (activeStep === 1 && grupos.length === 0) ||
                (activeStep === 2 && !currentGrupo) ||
                (activeStep === 3 && !currentPergunta)
              }
              variant="contained"
              onClick={() => setActiveStep((s) => Math.min(4, s + 1))}
            >
              Avançar
            </Button>
          </Box>

          <ChecklistPreview
            tituloChecklist={checklist.descricao_checklist}
            checklistId={checklist.id}
            grupos={grupos}
          />
        </Grid>
      </Grid>

      <GroupDialog
        open={openGroup}
        onClose={() => setOpenGroup(false)}
        onSubmit={handleAddGrupo}
      />
      <QuestionDialog
        open={openQuestion}
        onClose={() => setOpenQuestion(false)}
        onSubmit={handleAddPergunta}
        proximaOrdem={(currentGrupo?.perguntas.length || 0) + 1}
      />
      <AnswerDialog
        open={openAnswer}
        onClose={() => setOpenAnswer(false)}
        onSubmit={handleAddResposta}
        proximaOrdem={(currentPergunta?.respostas.length || 0) + 1}
      />
    </Box>
  );
}
